import { mdiAccount, mdiCalendarSync, mdiClockEditOutline, mdiShieldKey } from "@mdi/js"
import {
  buildViewNavigation,
  resolvePageEntry,
  type PageTabWithId,
  type ViewPageSnippets,
  type ViewPageSpec,
} from "@kontextwerk/web-sdk/core/navigation/viewNavigation"
import type { ProfileMenuPage } from "@kontextwerk/web-sdk/core/navigation/profileMenu"
import type { PermissionSectionConfig } from "../../_shared/_logic/permission-sections"
import { DEFAULT_PERMISSION_SECTIONS } from "@kontextwerk/control-web-frame/routes/OrgaView/subviews/User/logic/permissionSections"

type UserViewFrameContext = {
  user?: { id?: string | null } | null
  currentUserId?: string | null
  canViewCalendarIntegration?: boolean
  canDisconnectOwnCalendarIntegration?: boolean
  permissionSections?: PermissionSectionConfig[] | null
}

export type UserViewPaths = {
  general: string
  permissions: string
  integrations: string
  workingHours: string
}

export const buildUserViewPaths = (
  pathFor?: ((pageId: string) => string) | null,
): UserViewPaths => ({
  general: pathFor ? pathFor("general") : "",
  permissions: pathFor ? pathFor("permissions") : "",
  integrations: pathFor ? pathFor("integrations") : "",
  workingHours: pathFor ? pathFor("working-hours") : "",
})

const getGeneralTabs = (ctx: UserViewFrameContext): PageTabWithId[] => {
  const tabs: PageTabWithId[] = [
    { id: "profile", title: "Profil", widthType: "small" },
    { id: "password", title: "Passwort", widthType: "small" },
    { id: "security", title: "Sicherheit", widthType: "small" },
  ]

  if (ctx.currentUserId && ctx.user?.id && ctx.currentUserId === ctx.user.id) {
    tabs.push({ id: "sessions", title: "Sitzungen", widthType: "normal" })
  }

  return tabs
}

const getPermissionsTabs = (ctx: UserViewFrameContext): PageTabWithId[] => {
  const resolvedSections = ctx.permissionSections?.length
    ? ctx.permissionSections
    : DEFAULT_PERMISSION_SECTIONS
  const tabs: PageTabWithId[] = [{ id: "general", title: "Allgemein", widthType: "small" }]
  for (const section of resolvedSections) {
    if (section.id === "core") continue

    tabs.push({
      id: section.id,
      title: section.title,
      widthType: "small",
    })
  }

  return tabs
}

const getIntegrationsTabs = (ctx: UserViewFrameContext): PageTabWithId[] =>
  ctx.canViewCalendarIntegration || ctx.canDisconnectOwnCalendarIntegration
    ? [{ id: "integrations", title: "Integrationen", widthType: "small" }]
    : []

const getWorkingHoursTabs = (_ctx: UserViewFrameContext): PageTabWithId[] => [
  { id: "working-hours", title: "Arbeitszeiten", widthType: "small" },
]

const PAGE_SPECS: Array<ViewPageSpec<UserViewFrameContext, UserViewPaths>> = [
  {
    id: "general",
    title: "Allgemein",
    icon: mdiAccount,
    widthType: "small",
    resolvePath: (paths) => paths.general,
    getTabs: getGeneralTabs,
  },
  {
    id: "permissions",
    title: "Rolle & Berechtigungen",
    icon: mdiShieldKey,
    widthType: "small",
    resolvePath: (paths) => paths.permissions,
    getTabs: getPermissionsTabs,
  },
  {
    id: "integrations",
    title: "Integrationen",
    icon: mdiCalendarSync,
    widthType: "small",
    resolvePath: (paths) => paths.integrations,
    canOpen: (_capabilities, ctx) =>
      Boolean(ctx.canViewCalendarIntegration || ctx.canDisconnectOwnCalendarIntegration),
    getTabs: getIntegrationsTabs,
  },
  {
    id: "workingHours",
    title: "Arbeitszeiten",
    icon: mdiClockEditOutline,
    widthType: "small",
    resolvePath: (paths) => paths.workingHours,
    getTabs: getWorkingHoursTabs,
  },
]

export const buildUserViewNavigation = ({
  context,
  paths,
  snippets,
}: {
  context: UserViewFrameContext
  paths: UserViewPaths
  snippets: ViewPageSnippets
}) =>
  buildViewNavigation({
    specs: PAGE_SPECS,
    context,
    paths,
    capabilities: null,
    snippets,
  })

export const buildUserProfilePages = (
  userId: string,
  pathFor: (pageId: string) => string,
): ProfileMenuPage[] =>
  PAGE_SPECS.flatMap((spec) => {
    const page = resolvePageEntry({
      spec,
      context: {
        user: { id: userId },
        currentUserId: userId,
        canDisconnectOwnCalendarIntegration: true,
      },
      paths: buildUserViewPaths(pathFor),
      capabilities: null,
    })

    return page ? [{ id: page.id!, title: page.title, href: page.path, icon: page.icon }] : []
  })
